<script lang="ts">
  /**
   * Overlay nhãn "sơ đồ lắp đặt" (GĐ2a): nhãn tên chi tiết (HTML) + đường dẫn leader (SVG) phủ trên
   * canvas 3D. Vị trí tính lại mỗi frame qua SceneManager.onAfterRender + project() (render on-demand
   * nên chỉ chạy khi có frame mới). Xếp nhãn bằng hàm thuần layoutLabels (L/R ưu tiên, tràn T/B).
   * Click nhãn → onClick (zoom/ngắm, KHÔNG đổi lựa chọn); hover → onHover (tô nổi chi tiết).
   */
  import { onMount } from 'svelte';
  import { Vector3 } from 'three';
  import type { SceneManager } from '../core/SceneManager';
  import { layoutLabels, type LabelPlaced } from '../core/labelLayout';

  let {
    scene,
    items,
    onHover,
    onClick,
  }: {
    scene: SceneManager;
    items: { id: number; text: string }[];
    onHover: (id: number | null) => void;
    onClick: (id: number) => void;
  } = $props();

  let host: HTMLDivElement;
  const els: Record<number, HTMLElement> = {};
  let placed = $state<LabelPlaced[]>([]);
  const placedById = $derived(new Map(placed.map((p) => [p.id, p])));
  const _v = new Vector3();

  // action: giữ ref phần tử nhãn theo id để đo bề rộng/cao thực
  function measure(node: HTMLElement, id: number) {
    els[id] = node;
    return {
      destroy() {
        delete els[id];
      },
    };
  }

  function recompute() {
    if (!host || !scene) return;
    const W = host.clientWidth;
    const H = host.clientHeight;
    if (W < 2 || H < 2) return;
    const inputs = [];
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (const it of items) {
      const a = scene.partAnchorWorld(it.id);
      if (!a) continue;
      const p = scene.project(_v.set(a[0], a[1], a[2])); // px trong hệ canvas (= host vì phủ khít)
      const el = els[it.id];
      inputs.push({ id: it.id, ax: p.x, ay: p.y, w: el?.offsetWidth ?? 80, h: el?.offsetHeight ?? 22 });
      sx += p.x;
      sy += p.y;
      n++;
    }
    if (n === 0) {
      if (placed.length) placed = [];
      return;
    }
    placed = layoutLabels(inputs, { width: W, height: H, cx: sx / n, cy: sy / n });
  }

  onMount(() => {
    const unsub = scene.onAfterRender(recompute);
    requestAnimationFrame(recompute); // lần đầu — sau khi nhãn đã vào DOM để đo được
    return unsub;
  });

  // đổi tập nhãn → tính lại (đợi DOM cập nhật kích thước)
  $effect(() => {
    void items;
    requestAnimationFrame(recompute);
  });
</script>

<div class="label-host" bind:this={host} aria-hidden="true">
  <svg class="leaders" width="100%" height="100%">
    {#each placed as p (p.id)}
      <line x1={p.lx} y1={p.ly} x2={p.ax} y2={p.ay} />
      <circle cx={p.ax} cy={p.ay} r="2.5" />
    {/each}
  </svg>
  {#each items as it (it.id)}
    {@const p = placedById.get(it.id)}
    <button
      class="label"
      class:ready={!!p}
      use:measure={it.id}
      style={p ? `transform: translate(${p.x}px, ${p.y}px)` : ''}
      onmouseenter={() => onHover(it.id)}
      onmouseleave={() => onHover(null)}
      onfocus={() => onHover(it.id)}
      onblur={() => onHover(null)}
      onclick={() => onClick(it.id)}
    >
      {it.text}
    </button>
  {/each}
</div>

<style>
  .label-host {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none; /* không chặn orbit; chỉ nhãn bắt chuột */
    z-index: var(--z-overlay, 5);
  }
  .leaders {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
  .leaders line {
    stroke: var(--color-primary);
    stroke-width: 1;
    opacity: 0.7;
  }
  .leaders circle {
    fill: var(--color-primary);
  }
  .label {
    position: absolute;
    top: 0;
    left: 0;
    max-width: 180px;
    padding: 2px 7px;
    border: 1px solid color-mix(in srgb, var(--color-primary) 35%, var(--color-border));
    border-radius: var(--radius-control);
    background: var(--color-surface-glass, var(--color-surface));
    color: var(--color-text-primary);
    font: inherit;
    font-size: var(--font-size-caption);
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
    pointer-events: auto;
    box-shadow: var(--shadow-panel);
    backdrop-filter: blur(6px);
    opacity: 0; /* chưa xếp xong (chưa đo) → ẩn, tránh nhấp nháy ở (0,0) */
    transition: opacity 0.12s ease;
  }
  .label.ready {
    opacity: 1;
  }
  .label:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
</style>
